import type { Brand } from '../../types'
import { Icons } from '../icons'

/** BMC-Blau als Fallback, wenn keine Primärfarbe gesetzt ist. */
const BMC_BLUE = '#4A6CF7'

export interface BrandPreviewProps {
  data: Brand
}

/** Entfernt das Protokoll-Präfix für eine kompaktere Anzeige (vgl. Dummy). */
function stripProtocol(value: string): string {
  return value.replace(/^https?:\/\//, '')
}

/** Initialen aus dem Markennamen: erste Buchstaben der ersten zwei Wörter. */
function brandInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || '?'
  )
}

function BrandItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2.5 py-2.5">
      <span className="grid size-8 flex-none place-items-center rounded-[9px] bg-muted text-muted-foreground">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">{label}</div>
        <div className="truncate text-[13.5px] font-medium text-card-foreground">{value}</div>
      </div>
    </div>
  )
}

/**
 * Live-Vorschau des Brand-Profils aus dem aktuellen Formzustand (kontrolliert,
 * kein eigener Fetch), vgl. Dummy `updateBrandPreview`.
 *
 * Die Kopf-Farbe kommt aus `data.primaryColor` (dynamisch, inline-`style`);
 * Fallback ist BMC-Blau. Layout/Tokens: `theme.css` (Light+Dark). Die Klasse
 * `vcard` bleibt als stabiler Test-Hook erhalten.
 */
export function BrandPreview({ data }: BrandPreviewProps) {
  const name = data.brandName?.trim() || 'Markenname'
  const color = data.primaryColor || BMC_BLUE
  const initials = brandInitials(name)
  const description = data.brandDescription?.trim() ?? ''
  const website = stripProtocol(data.brandLink?.trim() ?? '')

  return (
    <div className="vcard overflow-hidden rounded-xl border bg-card shadow-sm">
      <div
        className="relative overflow-hidden px-5 pb-[18px] pt-[22px] text-center text-white"
        style={{
          backgroundImage: `linear-gradient(120deg, ${color}, color-mix(in srgb, ${color} 55%, #000))`,
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-8 -top-8 size-44 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,.22), transparent 62%)' }}
        />
        <div className="relative z-[1] mx-auto mb-3 grid size-[74px] place-items-center overflow-hidden rounded-[15px] border-2 border-white/25 bg-white/15 text-2xl font-bold text-white">
          {initials}
        </div>
        <div className="vname relative z-[1] text-[19px] font-bold">{name}</div>
        {data.signetImage?.trim() ? (
          <img className="relative z-[1] mx-auto mt-2 max-h-8 object-contain" src={data.signetImage} alt="" />
        ) : (
          data.sigent?.trim() && (
            <div className="relative z-[1] mt-0.5 text-[13px] text-white/80">{data.sigent}</div>
          )
        )}
      </div>
      <div className="px-4 pb-4 pt-3.5">
        <div className="divide-y divide-border">
          {description && <BrandItem icon={Icons.brand} label="Beschreibung" value={description} />}
          {website && <BrandItem icon={Icons.web} label="Website" value={website} />}
        </div>
      </div>
    </div>
  )
}
